import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'

interface UseContainerSizeConfig {
  containerElement?: RefObject<HTMLElement | null>
  defaultWidth?: number
  defaultHeight?: number
  /** Content box doesn't include padding, border, or margin. Border box includes padding and border. */
  boxType?: 'content-box' | 'border-box'
}

/**
 * Hook to get the width of a container element. Responds to changes in the container's width.
 */
export function useContainerSize({
  containerElement,
  defaultWidth = 300,
  defaultHeight = 200,
  boxType = 'content-box',
}: UseContainerSizeConfig) {
  const [containerWidth, setContainerWidth] = useState(defaultWidth)
  const [containerHeight, setContainerHeight] = useState(defaultHeight)
  const resizeObserver = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    resizeObserver.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (boxType === 'border-box') {
          setContainerWidth(entry.borderBoxSize[0].inlineSize)
          setContainerHeight(entry.borderBoxSize[0].blockSize)
        } else {
          setContainerWidth(entry.contentBoxSize[0].inlineSize)
          setContainerHeight(entry.contentBoxSize[0].blockSize)
        }
      }
    })
    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect()
      }
    }
  }, [boxType])

  useEffect(() => {
    if (containerElement && containerElement.current) {
      resizeObserver.current?.observe(containerElement.current)
      return () => {
        if (containerElement.current !== null) {
          resizeObserver.current?.unobserve(containerElement.current)
        }
      }
    }
    return undefined
  }, [containerElement])

  return { containerWidth, containerHeight }
}
